// Common style helpers - pure utility functions
export const createCommonStyles = (isDarkMode = true) => ({
  // Glassmorphism containers
  glassContainer: {
    background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.9)",
    borderRadius: 2,
    backdropFilter: "blur(10px)",
    border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
    boxShadow: isDarkMode ? "0 8px 32px rgba(0, 0, 0, 0.3)" : "0 8px 32px rgba(0, 0, 0, 0.1)",
  },

  glassContainerMedium: {
    background: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 0.95)",
    borderRadius: 2,
    backdropFilter: "blur(15px)",
    border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid rgba(0, 0, 0, 0.15)",
    boxShadow: isDarkMode ? "0 8px 32px rgba(0, 0, 0, 0.2)" : "0 8px 32px rgba(0, 0, 0, 0.1)",
  },

  glassContainerStrong: {
    background: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 1)",
    borderRadius: 2,
    backdropFilter: "blur(20px)",
    border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid rgba(0, 0, 0, 0.2)",
    boxShadow: isDarkMode ? "0 8px 32px rgba(0, 0, 0, 0.2)" : "0 8px 32px rgba(0, 0, 0, 0.1)",
  },

  // Add to commonStyles:
  glassSurface: {
    // slightly denser than glassContainerMedium for better contrast
    background: isDarkMode ? "rgba(20, 25, 55, 0.65)" : "rgba(255, 255, 255, 0.95)",
    borderRadius: 12,
    backdropFilter: "blur(14px)",
    border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.12)",
    boxShadow: isDarkMode 
      ? "0 10px 36px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 10px 36px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.8)",
  },

  glassSurfaceElevated: {
    background: isDarkMode ? "rgba(24, 30, 66, 0.75)" : "rgba(255, 255, 255, 1)",
    borderRadius: 14,
    backdropFilter: "blur(18px)",
    border: isDarkMode ? "1px solid rgba(255,255,255,0.16)" : "1px solid rgba(0, 0, 0, 0.16)",
    boxShadow: isDarkMode 
      ? "0 16px 48px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)"
      : "0 16px 48px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.8)",
  },

  // gradient "halo" stroke around cards — use via sx spread
  gradientStroke: {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: 14,
      padding: "1px",
      background:
        "linear-gradient(90deg, #3B82F6 0%, #1D4ED8 50%, #06B6D4 100%)",
      WebkitMask:
        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
      WebkitMaskComposite: "xor", // mask out the middle
      maskComposite: "exclude",
      pointerEvents: "none",
      opacity: 0.28,
    },
  },

  // Text with stronger contrast
  textHi: { color: isDarkMode ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.95)" },
  textMed: { color: isDarkMode ? "rgba(255,255,255,0.82)" : "rgba(0,0,0,0.82)" },
  textDim: { color: isDarkMode ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)" },

  // Small divider for dark glass
  dividerHairline: {
    borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
  },

  // Accent icon badge (use with accent color)
  accentBadge: (accent = "#06B6D4") => ({
    width: 64,
    height: 64,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
    border: isDarkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
    boxShadow: `0 8px 22px ${accent}44, inset 0 1px 0 ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
  }),

  // Text styles
  textPrimary: {
    color: isDarkMode ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.9)",
  },

  textSecondary: {
    color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)",
  },

  textMuted: {
    color: isDarkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)",
  },

  // Button styles
  gradientButton: {
    background: isDarkMode 
      ? "linear-gradient(90deg, #1E40AF 0%, #1D4ED8 48%, #0891B2 100%)"
      : "linear-gradient(90deg, #3B82F6 0%, #1D4ED8 48%, #06B6D4 100%)",
    color: "white",
    "&:hover": {
      background: isDarkMode
        ? "linear-gradient(90deg, #1E3A8A 0%, #1E40AF 48%, #0E7490 100%)"
        : "linear-gradient(90deg, #2563EB 0%, #1E40AF 48%, #0891B2 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.3)",
    },
    transition: "all 0.3s ease",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
  },

  outlinedButton: {
    color: isDarkMode ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.9)",
    borderColor: isDarkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
    backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 1)",
    "&:hover": {
      borderColor: isDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
      backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
    },
    transition: "all 0.2s ease",
  },

  cancelButton: {
    color: isDarkMode ? "rgba(255, 255, 255, 1)" : "rgba(0, 0, 0, 0.9)",
    borderColor: isDarkMode ? "rgba(148, 163, 184, 0.4)" : "rgba(0, 0, 0, 0.2)",
    backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.6)" : "rgba(0, 0, 0, 0.08)",
    "&:hover": {
      borderColor: isDarkMode ? "rgba(148, 163, 184, 0.6)" : "rgba(0, 0, 0, 0.4)",
      backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.8)" : "rgba(0, 0, 0, 0.15)",
    },
    transition: "all 0.2s ease",
  },

  // Table styles
  tableContainer: {
    background: isDarkMode ? "rgba(30, 41, 59, 0.9)" : "rgba(255, 255, 255, 1)",
    borderRadius: 2,
    border: isDarkMode ? "1px solid rgba(148, 163, 184, 0.2)" : "1px solid rgba(0, 0, 0, 0.1)",
  },

  tableHeader: {
    background: isDarkMode ? "rgba(30, 41, 59, 1)" : "rgba(0, 0, 0, 0.12)",
    borderBottom: isDarkMode ? "1px solid rgba(148, 163, 184, 0.3)" : "1px solid rgba(0, 0, 0, 0.2)",
    color: isDarkMode ? "rgba(255, 255, 255, 1)" : "rgba(0, 0, 0, 0.9)",
    fontWeight: 600,
  },

  tableRow: (index) => ({
    background: isDarkMode 
      ? (index % 2 === 0 ? "rgba(75, 85, 99, 0.3)" : "rgba(75, 85, 99, 0.5)")
      : (index % 2 === 0 ? "rgba(0, 0, 0, 0.02)" : "rgba(0, 0, 0, 0.05)"),
    "&:hover": {
      background: isDarkMode ? "rgba(75, 85, 99, 0.7)" : "rgba(0, 0, 0, 0.1)",
    },
  }),

  // Form styles
  formField: {
    "& .MuiOutlinedInput-root": {
      color: isDarkMode ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.9)",
      background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 1)",
      "& fieldset": {
        borderColor: isDarkMode ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.15)",
      },
      "&:hover fieldset": {
        borderColor: isDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#06B6D4",
      },
    },
    "& .MuiInputLabel-root": {
      color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)",
      "&.Mui-focused": {
        color: "#06B6D4",
      },
    },
  },

  // Select styles
  selectField: {
    color: isDarkMode ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.9)",
    background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 1)",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: isDarkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: isDarkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#06B6D4",
    },
    "& .MuiSvgIcon-root": {
      color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)",
    },
    "& .MuiSelect-select": {
      background: "transparent",
    },
  },

  selectMenu: {
    "& .MuiPaper-root": {
      background: isDarkMode ? "rgba(15, 23, 42, 0.98)" : "rgba(255, 255, 255, 1)",
      border: isDarkMode ? "1px solid rgba(148, 163, 184, 0.3)" : "1px solid rgba(0, 0, 0, 0.2)",
      boxShadow: isDarkMode 
        ? "0 8px 32px rgba(0, 0, 0, 0.6)" 
        : "0 8px 32px rgba(0, 0, 0, 0.15)",
    },
    "& .MuiMenuItem-root": {
      color: isDarkMode ? "rgba(255, 255, 255, 1)" : "rgba(0, 0, 0, 0.9)",
      background: isDarkMode ? "transparent" : "transparent",
      "&:hover": {
        background: isDarkMode ? "rgba(30, 41, 59, 0.6)" : "rgba(0, 0, 0, 0.05)",
      },
      "&.Mui-selected": {
        background: isDarkMode ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.1)",
        "&:hover": {
          background: isDarkMode ? "rgba(59, 130, 246, 0.4)" : "rgba(59, 130, 246, 0.2)",
        },
      },
    },
  },

  // Dialog styles
  dialogPaper: {
    background: isDarkMode ? "rgba(15, 23, 42, 0.98)" : "rgba(255, 255, 255, 1)",
    backdropFilter: "blur(25px)",
    border: isDarkMode ? "1px solid rgba(148, 163, 184, 0.3)" : "1px solid rgba(0, 0, 0, 0.15)",
    boxShadow: isDarkMode ? "0 25px 50px rgba(0, 0, 0, 0.8)" : "0 25px 50px rgba(0, 0, 0, 0.1)",
    color: isDarkMode ? "rgba(255, 255, 255, 1)" : "rgba(0, 0, 0, 0.9)",
  },

  dialogTitle: {
    background: isDarkMode ? "rgba(30, 41, 59, 0.8)" : "rgba(0, 0, 0, 0.08)",
    borderBottom: isDarkMode ? "1px solid rgba(148, 163, 184, 0.2)" : "1px solid rgba(0, 0, 0, 0.1)",
    color: isDarkMode ? "rgba(255, 255, 255, 1)" : "rgba(0, 0, 0, 0.9)",
  },

  dialogContent: {
    background: isDarkMode ? "rgba(15, 23, 42, 0.9)" : "rgba(255, 255, 255, 1)",
    borderTop: isDarkMode ? "1px solid rgba(148, 163, 184, 0.3)" : "1px solid rgba(0, 0, 0, 0.1)",
    borderBottom: isDarkMode ? "1px solid rgba(148, 163, 184, 0.3)" : "1px solid rgba(0, 0, 0, 0.1)",
  },

  dialogActions: {
    background: isDarkMode ? "rgba(15, 23, 42, 0.9)" : "rgba(255, 255, 255, 1)",
    borderTop: isDarkMode ? "1px solid rgba(148, 163, 184, 0.3)" : "1px solid rgba(0, 0, 0, 0.1)",
  },

  // Icon button styles
  iconButton: (color = "#06B6D4") => ({
    color: color,
    "&:hover": {
      background: `${color}20`,
    },
  }),

  // Alert styles
  alert: (severity = "error") => {
    const alertColors = {
      error: {
        background: "rgba(211, 47, 47, 0.1)",
        border: "1px solid rgba(211, 47, 47, 0.3)",
        color: "#ffcdd2",
      },
      success: {
        background: "rgba(34, 197, 94, 0.1)",
        border: "1px solid rgba(34, 197, 94, 0.3)",
        color: "#bbf7d0",
      },
      info: {
        background: "rgba(6, 182, 212, 0.1)",
        border: "1px solid rgba(6, 182, 212, 0.3)",
        color: "#a5f3fc",
      },
      warning: {
        background: "rgba(245, 158, 11, 0.1)",
        border: "1px solid rgba(245, 158, 11, 0.3)",
        color: "#fed7aa",
      },
    };

    return alertColors[severity] || alertColors.error;
  },
});

// Default commonStyles for backward compatibility
export const commonStyles = createCommonStyles(true);

// Spacing helpers
export const spacing = {
  xs: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 5,
};

// Border radius helpers
export const borderRadius = {
  small: 1,
  medium: 2,
  large: 3,
  round: "50%",
};

// Shadow helpers
export const shadows = {
  light: "0 4px 15px rgba(0, 0, 0, 0.2)",
  medium: "0 8px 32px rgba(0, 0, 0, 0.3)",
  heavy: "0 25px 50px rgba(0, 0, 0, 0.5)",
};

// Transition helpers
export const transitions = {
  fast: "all 0.2s ease",
  normal: "all 0.3s ease",
  slow: "all 0.5s ease",
};
